/**
 * GTM / dataLayer event helpers.
 * Push structured events to Google Tag Manager's dataLayer.
 * GTM forwards these to GA4 based on your container configuration.
 *
 * Usage:
 *   import { trackEvent } from '@/lib/gtm';
 *   trackEvent('sign_up', { method: 'google' });
 */

import { trackEvent as trackRudderEvent } from "./rudderstack-client";

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

type GtmEvent = Record<string, unknown> & { event: string };
type BrandStep =
  | "market_research"
  | "brand_strategy"
  | "brand_identity"
  | "logo_generation"
  | "brand_assets"
  | "brand_deck";
type BrandAssetKind = "icons" | "backgrounds" | "bundle";

function push(payload: GtmEvent) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

/** Generic event push */
export function trackEvent(event: string, params?: Record<string, unknown>) {
  push({ event, ...params });
  trackRudderEvent(event, params);
}

/** Account creation */
export function trackSignUp(method: "email" | "google") {
  push({ event: "sign_up", method });
  trackRudderEvent("User Signed Up", { method });
}

/** Workspace / onboarding complete */
export function trackTrialStart(userId: string) {
  push({ event: "trial_start", user_id: userId });
  trackRudderEvent("Trial Started", { user_id: userId });
}

/** Intent metric — user clicked 'Initialize Skillflow' (fires before auth check) */
export function trackGenerateWorkflowIntent(workflowType: string) {
  push({ event: "generate_workflow_intent", workflow_type: workflowType });
  trackRudderEvent("Workflow Intent", { workflow_type: workflowType });
}

/** Core value metric — user generated a workflow/pipeline */
export function trackGenerateWorkflow(workflowType: string) {
  push({ event: "generate_workflow", workflow_type: workflowType });
  trackRudderEvent("Workflow Generated", { workflow_type: workflowType });
}

/** Stripe subscription purchase (client-side, for redundancy alongside server-side) */
export function trackPurchase(plan: string, value: number, currency = "USD") {
  push({
    event: "purchase",
    ecommerce: {
      transaction_id: `${Date.now()}`,
      value,
      currency,
      items: [{ item_name: plan, price: value, quantity: 1 }],
    },
  });
  trackRudderEvent("Payment Completed", { plan, value, currency });
}

/** Copilot campaign started */
export function trackCoAgentStart(campaignName: string, agentId: string) {
  push({ event: "copilot_start", campaign_name: campaignName, agent_id: agentId });
  trackRudderEvent("Copilot Started", { campaign_name: campaignName, agent_id: agentId });
}

/** Credit top-up */
export function trackCreditTopUp(credits: number, value: number, currency = "USD") {
  push({
    event: "credit_topup",
    ecommerce: {
      value,
      currency,
      items: [{ item_name: "Credit Top-Up", price: value, quantity: credits }],
    },
  });
  trackRudderEvent("Credit Top-Up", { credits, value, currency });
}

/** Brand designer pipeline started */
export function trackBrandPipelineStarted(params: {
  brandName: string;
  genre: string;
  hasWebsite: boolean;
  competitorCount: number;
}) {
  trackEvent("brand_pipeline_started", {
    brand_name: params.brandName,
    genre: params.genre,
    has_website: params.hasWebsite,
    competitor_count: params.competitorCount,
  });
}

/** Brand pipeline step completion */
export function trackBrandStepCompleted(step: BrandStep, params?: Record<string, unknown>) {
  trackEvent("brand_step_completed", {
    step,
    ...(params ?? {}),
  });
}

/** Logo generation completion */
export function trackBrandLogoGenerated(params: { brandName: string; variantCount: number }) {
  trackEvent("brand_logo_generated", {
    brand_name: params.brandName,
    variant_count: params.variantCount,
  });
}

/** Logo selection */
export function trackBrandLogoSelected(params: {
  brandName: string;
  variantIndex: number;
  label?: string;
  aesthetic?: string;
}) {
  trackEvent("brand_logo_selected", {
    brand_name: params.brandName,
    variant_index: params.variantIndex,
    label: params.label,
    aesthetic: params.aesthetic,
  });
}

/** Asset generation completion */
export function trackBrandAssetsGenerated(params: {
  brandName: string;
  kind: BrandAssetKind;
  count: number;
}) {
  trackEvent("brand_assets_generated", {
    brand_name: params.brandName,
    asset_kind: params.kind,
    asset_count: params.count,
  });
}

/** Brand save completion */
export function trackBrandSaved(params: {
  brandId: string;
  brandName: string;
  setActive: boolean;
}) {
  trackEvent("brand_saved", {
    brand_id: params.brandId,
    brand_name: params.brandName,
    set_active: params.setActive,
  });
}

/** Deck open event */
export function trackBrandDeckOpened(params: { brandId: string; source: "launchpad" | "direct" }) {
  trackEvent("brand_deck_opened", {
    brand_id: params.brandId,
    source: params.source,
  });
}

/** Deck export lifecycle */
export function trackBrandDeckExportAttempted(params: { brandId: string }) {
  trackEvent("brand_deck_export_attempted", {
    brand_id: params.brandId,
  });
}

export function trackBrandDeckExportBlockedPaywall(params: {
  brandId?: string;
  target: "deck" | "assets";
  requiredPlan: "starter" | "pro" | "enterprise";
}) {
  trackEvent("brand_deck_export_blocked_paywall", {
    brand_id: params.brandId,
    target: params.target,
    required_plan: params.requiredPlan,
  });
}

export function trackBrandDeckExportSuccess(params: { brandId: string; slideCount: number }) {
  trackEvent("brand_deck_export_success", {
    brand_id: params.brandId,
    slide_count: params.slideCount,
  });
}

/** Paywall exposure */
export function trackPaywallViewed(params: {
  featureName: string;
  requiredPlan: "starter" | "pro" | "enterprise";
}) {
  trackEvent("paywall_viewed", {
    feature_name: params.featureName,
    required_plan: params.requiredPlan,
  });
}

/** GEO audit pipeline started */
export function trackGeoAuditStarted(params: {
  businessName: string;
  url: string;
  queryCount: number;
}) {
  trackEvent("geo_audit_started", {
    business_name: params.businessName,
    url: params.url,
    query_count: params.queryCount,
  });
}

/** GEO audit pipeline completed (client-side, fires when scorecard renders) */
export function trackGeoAuditCompleted(params: {
  businessName: string;
  geoScore: number;
  aiShareOfVoice: number;
  gapCount: number;
}) {
  trackEvent("geo_audit_completed", {
    business_name: params.businessName,
    geo_score: params.geoScore,
    ai_share_of_voice: params.aiShareOfVoice,
    gap_count: params.gapCount,
  });
}

/** Landing page CTA click */
export function trackLandingPageCta(params: {
  page: "brand-design" | "geo-audit";
  ctaType: "hero" | "mid" | "bottom";
  ctaText: string;
}) {
  trackEvent("lp_cta_click", {
    page: params.page,
    cta_type: params.ctaType,
    cta_text: params.ctaText,
  });
}
